// Compact panel that appears when an audio file with embedded ID3 tags is selected.
// Renders only if `meta` is non-null (parsed metadata).
// - Shows what was detected (title/artist/genre/lyrics/cover indicators)
// - "Autofill from MP3" button → calls onAutofill with the metadata; PARENT decides which fields to apply.
// - "Use this cover" button → calls onUseCover with a File built from the embedded image; PARENT applies.
//
// Parent should pass `formValues` so this component can show what will/won't be touched.

import React from "react";
import { Sparkles, Image as ImageIcon, FileText, Check } from "lucide-react";
import { coverDataUrlToFile } from "../lib/mp3meta";

export default function MetaAutofillBanner({
  meta,                 // { title, artist, album, genre, lyrics, coverDataUrl }
  formValues,           // current form values, so we can show "(skipped — already filled)" hints
  onAutofill,           // (metaSubset) => void  — only the fields that should be applied
  onUseCover,           // (File) => void
  hasUserCover,         // boolean — user already picked their own cover file
  appliedFields,        // Set<string> of fields already autofilled (avoids re-toast)
  appliedCover,         // boolean
}) {
  if (!meta) return null;

  const detected = [];
  if (meta.title) detected.push("title");
  if (meta.artist) detected.push("artist");
  if (meta.album) detected.push("album");
  if (meta.genre) detected.push("genre");
  if (meta.year) detected.push("year");
  if (meta.lyrics) detected.push("lyrics");
  if (meta.coverDataUrl) detected.push("cover art");

  const hasNothingUseful = detected.length === 0;

  // Decide which textual fields will actually get applied (don't overwrite manual input).
  const wouldFill = {
    title: meta.title && !formValues.title,
    artist: meta.artist && !formValues.artist,
    genre: meta.genre && !formValues.genre,
    lyrics: meta.lyrics && !formValues.lyrics,
  };

  const handleAutofill = () => {
    const subset = {};
    Object.entries(wouldFill).forEach(([k, ok]) => {
      if (ok) subset[k] = meta[k];
    });
    onAutofill?.(subset);
  };

  const handleUseCover = () => {
    if (!meta.coverDataUrl) return;
    const file = coverDataUrlToFile(meta.coverDataUrl, "embedded-cover");
    if (file) onUseCover?.(file);
  };

  return (
    <div
      className="rounded-lg border border-[#C5A059]/40 bg-[#1A1D24]/70 p-4 space-y-3"
      data-testid="meta-autofill-banner"
    >
      {hasNothingUseful ? (
        <div className="flex items-start gap-2 text-sm text-[#A1A4AB]">
          <FileText className="w-4 h-4 mt-0.5 text-[#6C707A]" strokeWidth={1.5} />
          <span>No metadata found in this MP3. You can fill in the fields manually below.</span>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-2 text-sm text-[#FDFBF7]">
            <Sparkles className="w-4 h-4 mt-0.5 text-[#C5A059]" strokeWidth={1.5} />
            <div>
              <span className="font-medium">Found ID3 tags:</span>{" "}
              <span className="text-[#A1A4AB]">{detected.join(" · ")}</span>
            </div>
          </div>

          {/* Cover preview if embedded */}
          {meta.coverDataUrl && (
            <div className="flex items-center gap-3">
              <img
                src={meta.coverDataUrl}
                alt="Embedded cover preview"
                className="w-16 h-16 rounded-md object-cover border border-[#C5A059]/30"
                data-testid="meta-autofill-cover-preview"
              />
              <div className="flex-1">
                <div className="text-xs text-[#A1A4AB] mb-1">
                  Embedded cover art {hasUserCover && "(you already picked your own — embedded won't override)"}
                </div>
                <button
                  type="button"
                  onClick={handleUseCover}
                  disabled={appliedCover}
                  className="text-xs overline px-3 py-1.5 rounded-full bg-[#1A1D24] border border-[#C5A059]/40 text-[#FDFBF7] hover:border-[#C5A059] disabled:opacity-50 inline-flex items-center gap-1.5"
                  data-testid="meta-autofill-use-cover-btn"
                >
                  {appliedCover ? (
                    <>
                      <Check className="w-3.5 h-3.5" strokeWidth={2} /> Cover applied
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-3.5 h-3.5" strokeWidth={1.5} /> Use this cover
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {!meta.lyrics && (
            <div className="text-xs text-[#6C707A]">
              Lyrics were not found in this MP3. You can paste them manually.
            </div>
          )}

          {/* Autofill button — only show if there's something to autofill that isn't already filled */}
          <div className="flex items-center justify-between gap-2 pt-1">
            <div className="text-xs text-[#6C707A]">
              {appliedFields && appliedFields.size > 0
                ? `Autofilled ${appliedFields.size} field${appliedFields.size === 1 ? "" : "s"}.`
                : "Click to fill blank fields. Manual input won't be overwritten."}
            </div>
            <button
              type="button"
              onClick={handleAutofill}
              disabled={!Object.values(wouldFill).some(Boolean)}
              className="text-xs overline px-3 py-1.5 rounded-full bg-[#C5A059] text-[#060709] hover:bg-[#DCC286] disabled:opacity-50 disabled:hover:bg-[#C5A059] inline-flex items-center gap-1.5"
              data-testid="meta-autofill-apply-btn"
            >
              <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> Autofill from MP3
            </button>
          </div>
        </>
      )}
    </div>
  );
}
